import { setTransientActionLabel } from '../transientSurfaceState';

/**
 * Auth form button loading state management.
 *
 * Disables the button, swaps its text content for a CSS-injected spinner
 * (via [data-loading] attribute), and restores the original text when done.
 * Reused across login / register / forgot-password / social-login flows
 * so the 1-2s API round-trip doesn't feel like the button is dead.
 *
 * When restoring (loading=false), if the button still has [data-locked]
 * (Turnstile challenge not yet solved), it stays disabled - the Turnstile
 * widget's onSuccess callback will remove data-locked and re-enable it.
 */
const originalMarkup = new WeakMap<HTMLButtonElement, string>();

export function setAuthButtonLoading(btn: HTMLButtonElement | null, loading: boolean, originalText?: string, loadingText?: string): void {
	if (!btn) return;
	if (loading) {
		btn.disabled = true;
		btn.dataset.loading = '';
		if (originalText) btn.dataset.originalText = originalText;
		if (!originalMarkup.has(btn)) originalMarkup.set(btn, btn.innerHTML);
		setTransientActionLabel(btn, loadingText ?? originalText ?? 'Working…');
	} else {
		delete btn.dataset.loading;
		const restore = originalText ?? btn.dataset.originalText;
		const markup = originalMarkup.get(btn);
		if (markup !== undefined) {
			btn.innerHTML = markup;
			originalMarkup.delete(btn);
		} else if (restore) {
			setTransientActionLabel(btn, restore);
		}
		delete btn.dataset.originalText;
		// Only re-enable if Turnstile hasn't locked the button. If data-locked
		// is present, the button must stay disabled until the challenge is solved.
		if (!btn.hasAttribute('data-locked')) {
			btn.disabled = false;
		}
	}
}
